import React, { lazy, Suspense, useState, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import MainLayout from '@/shared/layouts/MainLayout';
import { CssBaseline, CircularProgress, Box, ThemeProvider, createTheme } from '@mui/material';
import { apiClient } from '@/shared/api/client';
import { ColorModeContext } from '@/shared/contexts/theme-context';
import { ErrorContext } from '@/shared/contexts/error-context';
import './styles/app.css';

const AuthPage = lazy(() => import('@/pages/auth/AuthPage'));
const AdminPage = lazy(() => import('@/pages/admin/AdminPage'));
const DownloadPage = lazy(() => import('@/pages/download/DownloadPage'));
const InstructionsPage = lazy(() => import('@/pages/instructions/InstructionsPage'));

const PageLoader = () => (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
    </Box>
);

const validateTokenOnServer = () => {
    return apiClient.get('auth/token');
};
 
const App = () => {
    const [mode, setMode] = useState(() => {
        const savedMode = localStorage.getItem('themeMode');
        return savedMode || 'light';
    });

    const colorMode = useMemo(
        () => ({
            toggleColorMode: () => {
                setMode((prevMode) => {
                    const newMode = prevMode === 'light' ? 'dark' : 'light';
                    localStorage.setItem('themeMode', newMode);
                    return newMode;
                });
            },
        }),
        [],
    );

    const theme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode,
                    primary: { main: '#2563eb' },
                    secondary: { main: '#7c3aed' },
                    background: {
                        default: mode === 'light' ? '#f4f7fb' : '#0b1220',
                        paper: mode === 'light' ? '#ffffff' : '#111827',
                    },
                },
                shape: { borderRadius: 14 },
                typography: {
                    fontFamily: '"Inter", "Segoe UI", Roboto, sans-serif',
                    h4: { fontWeight: 800, letterSpacing: '-0.03em' },
                    h5: { fontWeight: 750, letterSpacing: '-0.02em' },
                    h6: { fontWeight: 700 },
                    button: { textTransform: 'none', fontWeight: 700 },
                },
                components: {
                    MuiCard: {
                        styleOverrides: {
                            root: {
                                border: mode === 'light' ? '1px solid #e5eaf2' : '1px solid #263244',
                                boxShadow: mode === 'light' ? '0 8px 30px rgba(15, 23, 42, 0.05)' : 'none',
                            },
                        },
                    },
                    MuiButton: {
                        defaultProps: { disableElevation: true },
                        styleOverrides: { root: { borderRadius: 10, minHeight: 40 } },
                    },
                    MuiTextField: {
                        defaultProps: { variant: 'outlined' },
                    },
                },
            }),
        [mode],
    );

    const [authState, setAuthState] = useState({
        isAuthenticated: false,
        isLoading: true,
    });

    const [globalError, setGlobalError] = useState(null);

    const checkAuth = useCallback(async () => {
        const token = localStorage.getItem('token');

        if (!token) {
            setAuthState({ isAuthenticated: false, isLoading: false });
            return;
        }
        
        if (token === 'dev-token') {
            setAuthState({ isAuthenticated: true, isLoading: false });
            return;
        }

        try {
            const decodedToken = jwtDecode(token);
            if (decodedToken.exp * 1000 < Date.now()) {
                throw new Error('Token expired');
            }
            const { data: profile } = await validateTokenOnServer();
            if (profile.role !== 'admin') {
                throw new Error('Недостаточно прав для входа в панель администратора');
            }
            setGlobalError(null);
            setAuthState({ isAuthenticated: true, isLoading: false });
        } catch (error) {
            console.error("Authentication check failed:", error.message);
            setGlobalError("Ошибка аутентификации: " + error.message);
            localStorage.removeItem('token');
            setAuthState({ isAuthenticated: false, isLoading: false });
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const handleLoginSuccess = () => {
        checkAuth();
    };
    
    const handleLogout = () => {
        localStorage.removeItem('token');
        setAuthState({ isAuthenticated: false, isLoading: false });
    }

    if (authState.isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }
 
    return (
        <ColorModeContext.Provider value={colorMode}>
            <ThemeProvider theme={theme}>
                <ErrorContext.Provider value={{ error: globalError, setError: setGlobalError }}>
                    <CssBaseline />
                    <BrowserRouter>

                        <Suspense fallback={<PageLoader />}>
                            <Routes>
                                <Route path="/download" element={<DownloadPage />} />
                                <Route 
                                    path="/login" 
                                    element={
                                        <MainLayout> {authState.isAuthenticated ? <Navigate to="/admin"/> :
                                            <AuthPage onLoginSuccess={handleLoginSuccess}/>}</MainLayout>
                                    } 
                                />
                                <Route 
                                    path="/admin"
                                    element={
                                        <MainLayout>
                                            {authState.isAuthenticated ? (
                                                <AdminPage onLogout={handleLogout}/>
                                            ) : (
                                                <Navigate to="/login" replace/>
                                            )}
                                            </MainLayout>
                                    }
                                />
                                <Route path="/" element={ <MainLayout> <InstructionsPage /> </MainLayout>} />
                                <Route path="*" element={<Navigate to="/" />} />
                            </Routes>
                        </Suspense>
                    </BrowserRouter>
                </ErrorContext.Provider>
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
}
 
export default App;
